import { Navigate, createBrowserRouter, useParams } from 'react-router';
import { RequireSession } from './api/session';
import { AppShell } from './layout/AppShell';
import { AiServiceDetailPage } from './pages/admin/AiServiceDetail';
import { AssistantSettingsPage } from './pages/admin/Assistant';
import { AiServicesPage } from './pages/admin/AiServices';
import { ServiceAccountsPage } from './pages/admin/ServiceAccounts';
import { AuditPage } from './pages/admin/Audit';
import { InvitationsPage } from './pages/admin/Invitations';
import { UserDetailPage } from './pages/admin/UserDetail';
import { UsersPage } from './pages/admin/Users';
import { AccessUnavailablePage } from './pages/auth/AccessUnavailable';
import { SignInPage } from './pages/auth/SignIn';
import { ActivityPage } from './pages/projects/Activity';
import { ContextPage } from './pages/projects/Context';
import { ContextVersionPage } from './pages/projects/ContextVersion';
import { DashboardPage } from './pages/projects/Dashboard';
import { NewProjectPage } from './pages/projects/NewProject';
import { ProjectsPage } from './pages/projects/Projects';
import { EnvironmentsPage } from './pages/settings/Environments';
import { GithubWorkflowsPage } from './pages/settings/GithubWorkflows';
import { MembersPage } from './pages/settings/Members';
import { ProjectSettingsPage } from './pages/settings/ProjectSettings';
import { RepositoryBuildersPage } from './pages/settings/RepositoryBuilders';
import { BoardPage } from './pages/tickets/Board';
import { ImplementationPage } from './pages/tickets/Implementation';
import { NewTicketPage } from './pages/tickets/NewTicket';
import { SearchPage } from './pages/tickets/Search';
import { TicketDetailPage } from './pages/tickets/TicketDetail';
import { ApprovalDetailPage } from './pages/workspace/ApprovalDetail';
import { ApprovalsPage } from './pages/workspace/Approvals';
import { NotificationsPage } from './pages/workspace/Notifications';
import { AttemptPage } from './pages/releases/Attempt';
import { NewReleasePage } from './pages/releases/NewRelease';
import { ReleaseDetailPage } from './pages/releases/ReleaseDetail';
import { ReleasesPage } from './pages/releases/Releases';
import { NewRollbackPage, RollbackPage } from './pages/releases/Rollback';
import { PageUnavailable } from './ui/feedback';
import { AiConnectionsPage } from './pages/account/AiConnections';
import { ConnectAgentPage } from './pages/auth/ConnectAgent';
import { TicketViewerPage, ViewerExpiredPage } from './pages/intake/TicketViewer';
import { IntakeDonePage, IntakeExpiredPage, IntakeLayout, IntakeNewPage } from './pages/intake/Intake';

/** Old AI service links keep working after the rename to AI agents. */
function LegacyAiServiceRedirect() {
  const { serviceId } = useParams();
  return <Navigate to={`/admin/ai-agents/${serviceId}`} replace />;
}

/** Old context links redirect to the settings location. */
function LegacyContextRedirect() {
  const { key, section } = useParams();
  return <Navigate to={`/projects/${key}/settings/context/${section ?? 'overview'}`} replace />;
}

function LegacyContextVersionRedirect() {
  const { key, section, versionId } = useParams();
  return (
    <Navigate
      to={`/projects/${key}/settings/context/${section ?? 'overview'}/versions/${versionId}`}
      replace
    />
  );
}

function ContextIndexRedirect() {
  const { key } = useParams();
  return <Navigate to={`/projects/${key}/settings/context/overview`} replace />;
}

/** Canonical application routes. Static segments (`new`) precede parameters. */
export const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/access-unavailable', element: <AccessUnavailablePage /> },
  {
    path: '/connect/authorize',
    element: (
      <RequireSession>
        <ConnectAgentPage />
      </RequireSession>
    ),
  },
  // JIRA-19: another platform's popup; authenticated by its intake session, never the member session.
  { path: '/viewer/expired', element: <ViewerExpiredPage /> },
  { path: '/viewer/tickets/:ticketKey', element: <TicketViewerPage /> },
  { path: '/intake/expired', element: <IntakeExpiredPage /> },
  {
    path: '/intake',
    element: <IntakeLayout />,
    children: [
      { path: 'new', element: <IntakeNewPage /> },
      { path: 'done/:number', element: <IntakeDonePage /> },
      { path: '*', element: <PageUnavailable /> },
    ],
  },
  {
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: [
      { path: '/', element: <Navigate to="/projects" replace /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/new', element: <NewProjectPage /> },
      { path: '/projects/:key', element: <DashboardPage /> },
      { path: '/search', element: <SearchPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/approvals', element: <ApprovalsPage /> },
      { path: '/approvals/:approvalId', element: <ApprovalDetailPage /> },
      { path: '/projects/:key/board', element: <BoardPage /> },
      { path: '/projects/:key/search', element: <SearchPage scoped /> },
      { path: '/projects/:key/tickets/new', element: <NewTicketPage /> },
      { path: '/projects/:key/tickets/:number', element: <TicketDetailPage /> },
      {
        path: '/projects/:key/tickets/:number/implementations/:implementationId',
        element: <ImplementationPage />,
      },
      { path: '/projects/:key/releases', element: <ReleasesPage /> },
      { path: '/projects/:key/releases/new', element: <NewReleasePage /> },
      { path: '/projects/:key/releases/:releaseId', element: <ReleaseDetailPage /> },
      { path: '/projects/:key/releases/:releaseId/attempts/:attemptId', element: <AttemptPage /> },
      { path: '/projects/:key/releases/:releaseId/rollbacks/new', element: <NewRollbackPage /> },
      { path: '/projects/:key/releases/:releaseId/rollbacks/:rollbackId', element: <RollbackPage /> },
      {
        path: '/projects/:key/releases/:releaseId/rollbacks/:rollbackId/attempts/:attemptId',
        element: <AttemptPage rollback />,
      },
      { path: '/projects/:key/context/:section', element: <LegacyContextRedirect /> },
      {
        path: '/projects/:key/context/:section/versions/:versionId',
        element: <LegacyContextVersionRedirect />,
      },
      { path: '/projects/:key/activity', element: <ActivityPage /> },
      { path: '/projects/:key/settings', element: <ProjectSettingsPage /> },
      { path: '/projects/:key/settings/members', element: <MembersPage /> },
      { path: '/projects/:key/settings/repository-builders', element: <RepositoryBuildersPage /> },
      { path: '/projects/:key/settings/environments', element: <EnvironmentsPage /> },
      { path: '/projects/:key/settings/github-workflows', element: <GithubWorkflowsPage /> },
      { path: '/projects/:key/settings/context', element: <ContextIndexRedirect /> },
      { path: '/projects/:key/settings/context/:section', element: <ContextPage /> },
      {
        path: '/projects/:key/settings/context/:section/versions/:versionId',
        element: <ContextVersionPage />,
      },
      { path: '/account/ai-connections', element: <AiConnectionsPage /> },
      { path: '/admin/users', element: <UsersPage /> },
      { path: '/admin/users/:userId', element: <UserDetailPage /> },
      { path: '/admin/invitations', element: <InvitationsPage /> },
      { path: '/admin/ai-agents', element: <AiServicesPage /> },
      { path: '/admin/ai-agents/:serviceId', element: <AiServiceDetailPage /> },
      { path: '/admin/service-accounts', element: <ServiceAccountsPage /> },
      { path: '/admin/service-accounts/:serviceId', element: <AiServiceDetailPage /> },
      { path: '/admin/ai-services', element: <Navigate to="/admin/ai-agents" replace /> },
      { path: '/admin/ai-services/:serviceId', element: <LegacyAiServiceRedirect /> },
      { path: '/admin/assistant', element: <AssistantSettingsPage /> },
      { path: '/admin/audit', element: <AuditPage /> },
      { path: '*', element: <PageUnavailable /> },
    ],
  },
]);
